import React from 'react';
import { motion } from 'framer-motion';
import FadingVideo from './FadingVideo';
import BlurText from './BlurText';
import { useCountryLink } from '../context/CountryContext';

const ArrowUpRight = ({ className = 'h-5 w-5' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 17L17 7" />
    <path d="M7 7h10v10" />
  </svg>
);

const PlayIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="6,4 20,12 6,20" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const GlobeIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14 14 0 0 1 0 18" />
    <path d="M12 3a14 14 0 0 0 0 18" />
  </svg>
);

const fadeUp = {
  initial: { filter: 'blur(10px)', opacity: 0, y: 20 },
  animate: { filter: 'blur(0px)', opacity: 1, y: 0 },
};

const NAV_LINKS = ['System', 'FAQ'];

const HeroSpace = () => {
  const { link } = useCountryLink();
  return (
    <section className="relative h-screen w-full bg-black overflow-hidden">
      {/* Background video */}
      <FadingVideo
        src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260418_080021_d598092b-c4c2-4e53-8e46-94cf9064cd50.mp4"
        className="absolute left-1/2 top-0 -translate-x-1/2 object-cover object-top z-0"
        style={{ width: '120%', height: '120%' }}
      />

      {/* Subtle amber tint for theme cohesion */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 70%, rgba(255,110,0,0.10) 0%, rgba(255,110,0,0) 55%)',
        }}
      />

      {/* Navbar (MBE International logo on left) */}
      <div className="fixed top-3 sm:top-4 left-0 right-0 z-50 px-3 sm:px-8 lg:px-16 flex items-center justify-between md:justify-between">
        <a href="#" aria-label="MBE International" className="flex items-center">
          <img
            src="https://customer-assets.emergentagent.com/job_theme-17/artifacts/ycj74wd2_PROFILE%20MBE%20INTERNATIONAL%20OREN%201.png"
            alt="MBE International logo"
            className="h-10 sm:h-12 w-auto object-contain"
            width="180"
            height="48"
            decoding="async"
            fetchPriority="high"
          />
        </a>

        {/* Desktop nav */}
        <div className="liquid-glass hidden md:flex items-center px-1.5 py-1.5 rounded-full">
          {NAV_LINKS.map((label) => (
            <a
              key={label}
              href={`#${label.toLowerCase()}`}
              className="px-3 py-2 text-sm font-medium text-white/90 hover:text-white transition-colors"
              style={{ fontFamily: "'Nacelle', sans-serif" }}
            >
              {label}
            </a>
          ))}
          <a
            href={link} target="_blank" rel="noopener noreferrer"
            className="ml-1 bg-white text-black inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap hover:bg-[#FFF1E0] transition-colors"
            style={{ fontFamily: "'Nacelle', sans-serif" }}
          >
            Get Access Now <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>

        {/* Mobile compact nav */}
        <div className="liquid-glass md:hidden flex items-center px-1 py-1 rounded-full">
          <a
            href="#system"
            className="px-3 py-1.5 text-[12px] font-medium text-white/90"
            style={{ fontFamily: "'Nacelle', sans-serif" }}
          >
            System
          </a>
          <a
            href="#faq"
            className="px-3 py-1.5 text-[12px] font-medium text-white/90"
            style={{ fontFamily: "'Nacelle', sans-serif" }}
          >
            FAQ
          </a>
          <a
            href={link} target="_blank" rel="noopener noreferrer"
            className="bg-white text-black inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap"
            style={{ fontFamily: "'Nacelle', sans-serif" }}
          >
            Get Access <ArrowUpRight className="h-3 w-3" />
          </a>
        </div>

        <div className="hidden md:block w-12 h-12 opacity-0 pointer-events-none" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center pt-24 sm:pt-20 pb-10 px-4 text-center">
          {/* Badge */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
            className="liquid-glass inline-flex items-center gap-2 pl-1 py-1 rounded-full"
          >
            <span
              className="bg-white text-black px-2.5 sm:px-3 py-0.5 sm:py-1 text-[10.5px] sm:text-xs font-semibold rounded-full"
              style={{ fontFamily: "'Nacelle', sans-serif" }}
            >
              New
            </span>
            <span
              className="text-[12px] sm:text-sm text-white/90 pr-3"
              style={{ fontFamily: "'Nacelle', sans-serif" }}
            >
              MBE Alert v2 · Now Live on Telegram
            </span>
          </motion.div>

          {/* Headline */}
          <div
            className="mt-6 sm:mt-7"
            style={{ fontFamily: "'Nacelle', sans-serif", fontWeight: 600 }}
          >
            <BlurText
              text="Catch Every Valid Bubble Zone Across The Market"
              className="text-[2.6rem] xs:text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-white leading-[0.88] sm:leading-[0.85] max-w-[20rem] sm:max-w-xl md:max-w-2xl lg:max-w-3xl tracking-[-1.5px] sm:tracking-[-2px] lg:tracking-[-3px]"
            />
          </div>

          {/* Subheading */}
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.8 }}
            className="mt-5 sm:mt-6 text-[13.5px] sm:text-sm md:text-base text-white/90 max-w-md sm:max-w-xl md:max-w-2xl font-light leading-snug px-2"
            style={{ fontFamily: "'Nacelle', sans-serif" }}
          >
            Stop staring at charts. MBE Alert pings your Telegram the moment a
            valid bubble zone forms. Pair, timeframe, and entry already
            structured. React on time, not late.
          </motion.p>

          {/* CTAs */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 1.1 }}
            className="flex flex-col xs:flex-row items-center gap-4 sm:gap-6 mt-6 sm:mt-7"
          >
            <a
              href={link} target="_blank" rel="noopener noreferrer"
              className="liquid-glass-strong inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13.5px] sm:text-sm font-medium text-white hover:text-[#FFB37A] transition-colors"
              style={{ fontFamily: "'Nacelle', sans-serif" }}
            >
              Get MBE Alert Now <ArrowUpRight className="h-4 w-4 sm:h-5 sm:w-5" />
            </a>
            <a
              href="#video-demo"
              className="inline-flex items-center gap-2 text-white text-[13.5px] sm:text-sm font-medium hover:text-[#FFA66B] transition-colors"
              style={{ fontFamily: "'Nacelle', sans-serif" }}
            >
              See How It Works <PlayIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 1.3 }}
            className="grid grid-cols-2 sm:flex sm:items-stretch gap-3 sm:gap-4 mt-8 sm:mt-10 w-full max-w-md sm:max-w-none sm:w-auto"
          >
            <div className="liquid-glass p-4 sm:p-5 w-full sm:w-[220px] rounded-[1.25rem] flex flex-col items-start text-left">
              <ClockIcon />
              <div className="mt-auto pt-4 sm:pt-6">
                <div
                  className="text-3xl sm:text-4xl text-white tracking-[-1px] leading-none"
                  style={{ fontFamily: "'Nacelle', sans-serif", fontWeight: 600 }}
                >
                  &lt; 5 Sec
                </div>
                <div
                  className="text-[11px] sm:text-xs text-white font-light mt-1.5 sm:mt-2"
                  style={{ fontFamily: "'Nacelle', sans-serif" }}
                >
                  Alert-to-Action Decision Time
                </div>
              </div>
            </div>
            <div className="liquid-glass p-4 sm:p-5 w-full sm:w-[220px] rounded-[1.25rem] flex flex-col items-start text-left">
              <GlobeIcon />
              <div className="mt-auto pt-4 sm:pt-6">
                <div
                  className="text-3xl sm:text-4xl text-white tracking-[-1px] leading-none"
                  style={{ fontFamily: "'Nacelle', sans-serif", fontWeight: 600 }}
                >
                  5,000+
                </div>
                <div
                  className="text-[11px] sm:text-xs text-white font-light mt-1.5 sm:mt-2"
                  style={{ fontFamily: "'Nacelle', sans-serif" }}
                >
                  Traders Across 3 Continents
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSpace;
